import React, { useMemo } from 'react';
import {
  Box,
  Text,
  HStack,
  VStack,
  Icon,
  Skeleton,
  Badge,
  useColorModeValue,
  Tooltip,
  Progress,
  Divider,
  SimpleGrid,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { 
  FaCloud, 
  FaCloudSun, 
  FaSun, 
  FaCloudRain, 
  FaSnowflake, 
  FaWind, 
  FaTint, 
  FaEye, 
  FaThermometerHalf,
  FaExclamationTriangle,
  FaMoon,
} from 'react-icons/fa';
import api from '../../services/api';

interface MatchWeatherData {
  weather: {
    location: {
      name: string;
      region: string;
      country: string;
    };
    current: {
      last_updated: string;
      temp_c: number;
      temp_f: number;
      is_day: number;
      condition: {
        text: string;
        icon: string;
        code: number;
      };
      wind_mph: number;
      wind_kph: number;
      wind_degree: number;
      wind_dir: string;
      pressure_mb: number;
      pressure_in: number;
      precip_mm: number;
      precip_in: number;
      humidity: number;
      cloud: number;
      feelslike_c: number;
      feelslike_f: number;
      vis_km: number;
      vis_miles: number;
      uv: number;
      gust_mph: number;
      gust_kph: number;
    };
    forecast: {
      forecastday: Array<{
        date: string;
        date_epoch: number;
        day: {
          maxtemp_c: number;
          maxtemp_f: number;
          mintemp_c: number;
          mintemp_f: number;
          avgtemp_c: number;
          avgtemp_f: number;
          maxwind_mph: number;
          maxwind_kph: number;
          totalprecip_mm: number;
          totalprecip_in: number;
          totalsnow_cm: number;
          avgvis_km: number;
          avgvis_miles: number;
          avghumidity: number;
          daily_will_it_rain: number;
          daily_chance_of_rain: number;
          daily_will_it_snow: number;
          daily_chance_of_snow: number;
          condition: {
            text: string;
            icon: string;
            code: number;
          };
          uv: number;
        };
        astro: {
          sunrise: string;
          sunset: string;
          moonrise: string;
          moonset: string;
          moon_phase: string;
          moon_illumination: number;
          is_moon_up: number;
          is_sun_up: number;
        };
        hour: Array<{
          time_epoch: number;
          time: string;
          temp_c: number;
          temp_f: number;
          is_day: number;
          condition: {
            text: string;
            icon: string;
            code: number;
          };
          wind_mph: number;
          wind_kph: number;
          wind_degree: number;
          wind_dir: string;
          pressure_mb: number;
          pressure_in: number;
          precip_mm: number;
          precip_in: number;
          humidity: number;
          cloud: number;
          feelslike_c: number;
          feelslike_f: number;
          windchill_c: number;
          windchill_f: number;
          heatindex_c: number;
          heatindex_f: number;
          dewpoint_c: number;
          dewpoint_f: number;
          will_it_rain: number;
          chance_of_rain: number;
          will_it_snow: number;
          chance_of_snow: number;
          vis_km: number;
          vis_miles: number;
          gust_mph: number;
          gust_kph: number;
          uv: number;
        }>;
      }>;
    };
  };
  match_time: string;
  closest_hour: {
    time_epoch: number;
    time: string;
    temp_c: number;
    temp_f: number;
    is_day: number;
    condition: {
      text: string;
      icon: string;
      code: number;
    };
    wind_mph: number;
    wind_kph: number;
    wind_degree: number;
    wind_dir: string;
    pressure_mb: number;
    pressure_in: number;
    precip_mm: number;
    precip_in: number;
    humidity: number;
    cloud: number;
    feelslike_c: number;
    feelslike_f: number;
    windchill_c: number;
    windchill_f: number;
    heatindex_c: number;
    heatindex_f: number;
    dewpoint_c: number;
    dewpoint_f: number;
    will_it_rain: number;
    chance_of_rain: number;
    will_it_snow: number;
    chance_of_snow: number;
    vis_km: number;
    vis_miles: number;
    gust_mph: number;
    gust_kph: number;
    uv: number;
  };
}

interface MatchWeatherProps {
  matchDateTime: string;
  venue?: string;
  isHomeMatch: boolean;
  matchHasStarted: boolean;
  delayLoad?: boolean; // New prop to control when to load weather
}

// Czech and English translations
const translations = {
  cs: {
    weather: 'Počasí',
    forecast: 'Předpověď počasí',
    temperature: 'Teplota',
    feelsLike: 'Pocitově',
    wind: 'Vítr',
    humidity: 'Vlhkost',
    visibility: 'Viditelnost',
    pressure: 'Tlak',
    precipitation: 'Srážky',
    chanceOfRain: 'Šance na déšť',
    chanceOfSnow: 'Šance na sníh',
    atMatchTime: 'V čase zápasu',
    weatherUnavailable: 'Předpověď počasí není dostupná',
    forecastNotAvailable: 'Předpověď není dostupná pro tento zápas',
    tooFarInFuture: 'Zápas je příliš daleko v budoucnosti',
    matchInPast: 'Zápas již proběhl',
    loading: 'Načítám počasí...',
  },
  en: {
    weather: 'Weather',
    forecast: 'Weather Forecast',
    temperature: 'Temperature',
    feelsLike: 'Feels Like',
    wind: 'Wind',
    humidity: 'Humidity',
    visibility: 'Visibility',
    pressure: 'Pressure',
    precipitation: 'Precipitation',
    chanceOfRain: 'Rain Chance',
    chanceOfSnow: 'Snow Chance',
    atMatchTime: 'At Match Time',
    weatherUnavailable: 'Weather forecast unavailable',
    forecastNotAvailable: 'Forecast not available for this match',
    tooFarInFuture: 'Match is too far in the future',
    matchInPast: 'Match has already taken place',
    loading: 'Loading weather...',
  },
};

const getWeatherIcon = (condition: string, isDay: number) => {
  const lowerCondition = condition.toLowerCase();
  
  if (lowerCondition.includes('sunny') || lowerCondition.includes('clear')) {
    return isDay ? FaSun : FaMoon;
  }
  if (lowerCondition.includes('partly cloudy') || lowerCondition.includes('partly sunny')) {
    return FaCloudSun;
  }
  if (lowerCondition.includes('cloudy') || lowerCondition.includes('overcast')) {
    return FaCloud;
  }
  if (lowerCondition.includes('rain') || lowerCondition.includes('drizzle') || lowerCondition.includes('shower')) {
    return FaCloudRain;
  }
  if (lowerCondition.includes('snow') || lowerCondition.includes('sleet') || lowerCondition.includes('blizzard')) {
    return FaSnowflake;
  }
  
  return FaCloud;
};

const getWindDirection = (degree: number) => {
  const directions = ['S', 'SV', 'V', 'JV', 'J', 'JZ', 'Z', 'SZ'];
  const index = Math.round(degree / 45) % 8;
  return directions[index];
};

export const MatchWeather: React.FC<MatchWeatherProps> = ({ 
  matchDateTime, 
  venue, 
  isHomeMatch, 
  matchHasStarted,
  delayLoad = false
}) => {
  const { i18n } = useTranslation();
  const t = translations[i18n.language as keyof typeof translations] || translations.cs;
  
  const bgCard = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  // Only show weather for home matches that haven't started yet
  if (!isHomeMatch || matchHasStarted) {
    return null;
  }

  const { data: weatherData, isLoading, error } = useQuery<MatchWeatherData>({
    queryKey: ['matchWeather', matchDateTime, venue],
    queryFn: async () => {
      const params = new URLSearchParams({
        match_datetime: matchDateTime,
        // Only pass location parameter if it's not a home match (home matches use club location)
        ...(venue && { location: venue }),
      });
      
      const response = await api.get(`/weather/match?${params}`);
      return response.data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes cache
    enabled: isHomeMatch && !matchHasStarted && !delayLoad, // Disable if delayLoad is true
  });

  if (isLoading) {
    return (
      <Box
        bg={bgCard}
        p={4}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={borderColor}
        mt={4}
      >
        <VStack spacing={3} align="stretch">
          <Text fontSize="sm" fontWeight="bold" color={textColor}>
            {t.weather}
          </Text>
          <Skeleton height="20px" width="60%" />
          <Skeleton height="16px" width="40%" />
          <Skeleton height="16px" width="50%" />
        </VStack>
      </Box>
    );
  }

  if (error || !weatherData) {
    // Check for specific error messages to provide better user feedback
    let errorMessage = t.weatherUnavailable;
    const errorString = String(error || '');
    if (errorString.includes('too far in the future')) {
      errorMessage = t.tooFarInFuture;
    } else if (errorString.includes('match is in the past')) {
      errorMessage = t.matchInPast;
    } else if (errorString.includes('no location specified')) {
      errorMessage = t.forecastNotAvailable;
    }

    return (
      <Box
        bg={bgCard}
        p={4}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={borderColor}
        mt={4}
      >
        <VStack spacing={2} align="center">
          <Icon as={FaExclamationTriangle} boxSize={6} color="orange.400" />
          <Text fontSize="sm" color="gray.500" textAlign="center">
            {errorMessage}
          </Text>
        </VStack>
      </Box>
    );
  }

  const { weather, closest_hour } = weatherData;
  const currentIcon = getWeatherIcon(weather.current.condition.text, weather.current.is_day);
  const matchIcon = getWeatherIcon(closest_hour.condition.text, closest_hour.is_day);

  return (
    <Box
      bg={bgCard}
      p={4}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      mt={4}
    >
      <VStack spacing={4} align="stretch">
        {/* Header */}
        <HStack spacing={2} align="center">
          <Icon as={FaCloud} boxSize={4} color="blue.500" />
          <Text fontSize="sm" fontWeight="bold">
            {t.forecast}
          </Text>
          <Badge fontSize="xs" colorScheme="blue" variant="subtle">
            {weather.location.name}
          </Badge>
        </HStack>

        <Divider />

        {/* Current Weather */}
        <HStack spacing={3} align="center">
          <Icon as={currentIcon} boxSize={8} color="blue.400" />
          <VStack spacing={1} align="start" flex={1}>
            <Text fontSize="lg" fontWeight="bold">
              {Math.round(weather.current.temp_c)}°C
            </Text>
            <Text fontSize="xs" color={textColor}>
              {t.feelsLike} {Math.round(weather.current.feelslike_c)}°C
            </Text>
          </VStack>
          <VStack spacing={1} align="end">
            <Text fontSize="xs" color={textColor}>{weather.current.condition.text}</Text>
            {(closest_hour.will_it_rain > 0 || closest_hour.chance_of_rain > 0) && (
              <Badge size="xs" colorScheme="blue">
                {t.chanceOfRain}: {closest_hour.chance_of_rain}%
              </Badge>
            )}
            {(closest_hour.will_it_snow > 0 || closest_hour.chance_of_snow > 0) && (
              <Badge size="xs" colorScheme="cyan">
                {t.chanceOfSnow}: {closest_hour.chance_of_snow}%
              </Badge>
            )}
          </VStack>
        </HStack>

        {/* Weather at Match Time */}
        <Box>
          <Text fontSize="xs" fontWeight="bold" color={textColor} mb={2}>
            {t.atMatchTime}
          </Text>
          <HStack spacing={3} align="center" bg={useColorModeValue('gray.50', 'gray.700')} p={3} borderRadius="md">
            <Icon as={matchIcon} boxSize={6} color="blue.400" />
            <VStack spacing={1} align="start" flex={1}>
              <Text fontSize="md" fontWeight="bold">
                {Math.round(closest_hour.temp_c)}°C
              </Text>
              <Text fontSize="xs" color={textColor}>
                {closest_hour.condition.text}
              </Text>
            </VStack>
            <HStack spacing={4} fontSize="xs" color={textColor}>
              <HStack spacing={1}>
                <Icon as={FaWind} boxSize={3} />
                <Text>{closest_hour.wind_kph} km/h</Text>
              </HStack>
              <HStack spacing={1}>
                <Icon as={FaTint} boxSize={3} />
                <Text>{closest_hour.humidity}%</Text>
              </HStack>
            </HStack>
          </HStack>
        </Box>

        {/* Additional Details */}
        <SimpleGrid columns={2} spacing={2} fontSize="xs">
          <HStack spacing={2}>
            <Icon as={FaWind} boxSize={3} color={textColor} />
            <Text color={textColor}>{t.wind}: {weather.current.wind_kph} km/h {getWindDirection(weather.current.wind_degree)}</Text>
          </HStack>
          <HStack spacing={2}>
            <Icon as={FaTint} boxSize={3} color={textColor} />
            <Text color={textColor}>{t.humidity}: {weather.current.humidity}%</Text>
          </HStack>
          <HStack spacing={2}>
            <Icon as={FaEye} boxSize={3} color={textColor} />
            <Text color={textColor}>{t.visibility}: {weather.current.vis_km} km</Text>
          </HStack>
          <HStack spacing={2}>
            <Icon as={FaThermometerHalf} boxSize={3} color={textColor} />
            <Text color={textColor}>{t.pressure}: {weather.current.pressure_mb} hPa</Text>
          </HStack>
        </SimpleGrid>
      </VStack>
    </Box>
  );
};

export default MatchWeather;
